import type { ReactNode } from "react"
import { ChevronLeft, ChevronRight, Leaf, Snowflake, Sprout, Sun } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { listKeys } from "../shared"

// Box-style date pickers used instead of dropdowns everywhere a year or month is chosen.

const PERSIAN = "۰۱۲۳۴۵۶۷۸۹"
const fa = (value: number) => String(value).replace(/\d/g, (digit) => PERSIAN[Number(digit)]!)
const MONTHS = ["فروردین", "اردیبهشت", "خرداد", "تیر", "مرداد", "شهریور", "مهر", "آبان", "آذر", "دی", "بهمن", "اسفند"]

export const SEASONS = [
  { name: "بهار", months: [1, 2, 3], icon: Sprout, tone: "text-emerald-700" },
  { name: "تابستان", months: [4, 5, 6], icon: Sun, tone: "text-amber-600" },
  { name: "پاییز", months: [7, 8, 9], icon: Leaf, tone: "text-orange-700" },
  { name: "زمستان", months: [10, 11, 12], icon: Snowflake, tone: "text-sky-700" },
]

/** «۱۴۰۴-۱۴۰۵» for fiscal years (Mehr → Shahrivar). */
export const fiscalChip = (year: number) => `${fa(year)}-${fa(year + 1)}`

/** Year chips with previous/next arrows. */
export function YearPicker({ years, value, onChange, label = (year) => fa(year), ariaLabel = "سال" }: {
  years: number[]; value: number; onChange: (year: number) => void; label?: (year: number) => string; ariaLabel?: string
}) {
  const index = years.indexOf(value)
  return (
    <div className="flex items-center gap-2">
      <Button variant="outline" size="icon" className="size-8" aria-label="سال قبل" disabled={index <= 0} onClick={() => onChange(years[index - 1]!)}><ChevronRight className="size-4" /></Button>
      <div className="flex gap-1 rounded-lg border bg-muted p-1" role="radiogroup" aria-label={ariaLabel} onKeyDown={(event) => listKeys(event, "radio", (index) => { const next = years[index]; if (next !== undefined) onChange(next) }, false)}>
        {years.map((year) => (
          <button key={year} type="button" role="radio" aria-checked={year === value} tabIndex={year === value ? 0 : -1} onClick={() => onChange(year)}
            className={cn("rounded-md px-3 py-1 text-sm font-medium whitespace-nowrap tabular-nums transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none", year === value ? "bg-background text-foreground shadow-sm" : "hidden text-muted-foreground hover:text-foreground sm:block")}>
            {label(year)}
          </button>
        ))}
      </div>
      <Button variant="outline" size="icon" className="size-8" aria-label="سال بعد" disabled={index < 0 || index >= years.length - 1} onClick={() => onChange(years[index + 1]!)}><ChevronLeft className="size-4" /></Button>
    </div>
  )
}

/** Fiscal years around `around` (3 back, 2 forward). */
export const fiscalYears = (around: number) => Array.from({ length: 6 }, (_, index) => around - 3 + index)

export type MonthOption = { year: number; month: number; disabled?: boolean; note?: ReactNode }

/** Year chips + the year's months as tiles grouped by season. */
export function MonthPicker({ options, value, onChange }: { options: MonthOption[]; value: { year: number; month: number } | null; onChange: (value: { year: number; month: number }) => void }) {
  const years = [...new Set(options.map((option) => option.year))].sort((a, b) => a - b)
  const year = value?.year ?? years.at(-1) ?? 0
  const byKey = new Map(options.map((option) => [`${option.year}-${option.month}`, option]))
  if (!years.length) return <p className="text-sm text-muted-foreground">ماهی برای انتخاب وجود ندارد.</p>
  return (
    <div className="grid gap-3">
      <YearPicker years={years} value={year} onChange={(next) => {
        const first = options.filter((option) => option.year === next && !option.disabled).at(-1)
        if (first) onChange({ year: first.year, month: first.month })
      }} />
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        {SEASONS.map((season) => (
          <div key={season.name} className="rounded-lg border p-2">
            <p className="mb-1.5 flex items-center gap-1 text-xs font-medium text-muted-foreground"><season.icon className={cn("size-3.5", season.tone)} /> {season.name}</p>
            <div className="grid grid-cols-3 gap-1.5">
              {season.months.map((month) => {
                const option = byKey.get(`${year}-${month}`)
                const selected = value?.year === year && value.month === month
                const disabled = !option || option.disabled
                return (
                  <button key={month} type="button" disabled={disabled} aria-pressed={selected} onClick={() => onChange({ year, month })}
                    className={cn("flex min-h-12 flex-col items-center justify-center rounded-md border px-1 py-1.5 text-xs transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
                      selected ? "border-primary bg-primary text-primary-foreground" : disabled ? "border-dashed text-muted-foreground opacity-40" : "bg-background hover:border-primary")}>
                    <span className="font-medium">{MONTHS[month - 1]}</span>
                    {option?.note && <span className="text-xs opacity-80">{option.note}</span>}
                  </button>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
